import { useState, useCallback, useEffect, KeyboardEvent } from 'react'
import { SuggestionItem } from '../types'

interface UseSuggestionsKeyboardProps {
  suggestions: SuggestionItem[]
  isActive: boolean
  onSelect: (item: SuggestionItem) => void
  onClose: () => void
}

interface UseSuggestionsKeyboardResult {
  selectedIndex: number
  handleKeyDown: (event: KeyboardEvent) => boolean
}

/**
 * Hook for handling keyboard navigation within the suggestions popover.
 * Handles ArrowUp/ArrowDown for navigation, Enter for selection, and Escape to close.
 *
 * Returns whether the key event was handled (to prevent default behavior).
 */
export function useSuggestionsKeyboard({
  suggestions,
  isActive,
  onSelect,
  onClose,
}: UseSuggestionsKeyboardProps): UseSuggestionsKeyboardResult {
  const [selectedIndex, setSelectedIndex] = useState(0)

  // Reset selected index when suggestions change
  useEffect(() => {
    setSelectedIndex(0)
  }, [suggestions])

  const handleKeyDown = useCallback(
    (event: KeyboardEvent): boolean => {
      if (!isActive || suggestions.length === 0) {
        return false
      }

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault()
          setSelectedIndex(prev =>
            prev < suggestions.length - 1 ? prev + 1 : 0
          )
          return true

        case 'ArrowUp':
          event.preventDefault()
          setSelectedIndex(prev =>
            prev > 0 ? prev - 1 : suggestions.length - 1
          )
          return true

        case 'Enter':
        case 'Tab':
          event.preventDefault()
          if (suggestions[selectedIndex]) {
            onSelect(suggestions[selectedIndex])
          }
          return true

        case 'Escape':
          event.preventDefault()
          event.stopPropagation()
          onClose()
          return true

        default:
          return false
      }
    },
    [isActive, suggestions, selectedIndex, onSelect, onClose]
  )

  return {
    selectedIndex,
    handleKeyDown,
  }
}
